import { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Toolbar,
  CssBaseline,
  IconButton,
  AppBar,
  Typography,
  ListItemButton,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";

import LogoutIcon from "@mui/icons-material/Logout";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import SchoolIcon from "@mui/icons-material/School";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CampaignIcon from "@mui/icons-material/Campaign";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import {
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { DashboardSection } from "./dashboard";
import { UserSection } from "./users";
import { SaleReportSection } from "./sale-report";
import { SalesTransactionSection } from "./sale-transactions";
import { SaleReportSection as CommunitySaleReportSection } from "./community-sale-report";
import { UserTreeSection } from "./user-tree";
import { SubscriptionsSection } from "./subscriptions";
import { CoursesSection } from "./courses";
import { CourseSeasonsSection } from "./course-seasons";
import { CourseItemsSection } from "./course-items";
import { BDSection } from "./bd";
import { BDDetailsSection } from "./bd-details";
import { MiniAppNotificationsSection } from "./miniapp-notifications";
import { BotNotificationsSection } from "./bot-notifications";
import { getToken, removeToken } from "../utils";
import { history } from "../utils/axios-instance";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"; // Import React Query
import { MenuIcon as IconWrapper } from "../components";

const drawerWidth = 240;

const queryClient = new QueryClient(); // Initialize React Query Client

export default function Home() {
  const navigate = useNavigate();
  if (!getToken()) navigate("/login");
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    {
      text: "Dashboard",
      icon: (
        <IconWrapper
          icon={<HomeIcon />}
          color="#1976d2"
          bgColor="rgba(25, 118, 210, 0.1)"
        />
      ),
      path: "/",
    },
    {
      text: "Users",
      icon: (
        <IconWrapper
          icon={<PeopleIcon />}
          color="#4caf50"
          bgColor="rgba(76, 175, 80, 0.1)"
        />
      ),
      path: "/user",
    },
    {
      text: "BD Program",
      icon: (
        <IconWrapper
          icon={<BusinessCenterIcon />}
          color="#607d8b"
          bgColor="rgba(96, 125, 139, 0.1)"
        />
      ),
      path: "/bd",
    },
    {
      text: "Sale Report",
      icon: (
        <IconWrapper
          icon={<AssessmentIcon />}
          color="#ff9800"
          bgColor="rgba(255, 152, 0, 0.1)"
        />
      ),
      path: "/sale-report",
    },
    {
      text: "Community Sale Report",
      icon: (
        <IconWrapper
          icon={<AssessmentIcon />}
          color="#e91e63"
          bgColor="rgba(233, 30, 99, 0.1)"
        />
      ),
      path: "/community-sale-report",
    },
    {
      text: "Sale Transactions",
      icon: (
        <IconWrapper
          icon={<PointOfSaleIcon />}
          color="#9c27b0"
          bgColor="rgba(156, 39, 176, 0.1)"
        />
      ),
      path: "/sales",
    },
    {
      text: "Subscriptions",
      icon: (
        <IconWrapper
          icon={<SubscriptionsIcon />}
          color="#00bcd4"
          bgColor="rgba(0, 188, 212, 0.1)"
        />
      ),
      path: "/subscriptions",
    },
    {
      text: "Courses",
      icon: (
        <IconWrapper
          icon={<SchoolIcon />}
          color="#5c6bc0"
          bgColor="rgba(92, 107, 192, 0.1)"
        />
      ),
      path: "/courses",
    },
    {
      text: "MiniApp Notifications",
      icon: (
        <IconWrapper
          icon={<CampaignIcon />}
          color="#8e24aa"
          bgColor="rgba(142, 36, 170, 0.1)"
        />
      ),
      path: "/miniapp-notifications",
    },
    {
      text: "Bot Notifications",
      icon: (
        <IconWrapper
          icon={<SmartToyIcon />}
          color="#00897b"
          bgColor="rgba(0, 137, 123, 0.1)"
        />
      ),
      path: "/bot-notifications",
    },
  ];

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar />
      <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src="/mikey-logo.webp"
          alt="Money Mike Logo"
          sx={{ height: 60, width: "auto" }}
        />
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                borderRadius: "8px",
                margin: "4px 8px",
                minHeight: "48px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  "& .MuiTypography-root": {
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LogoutIcon sx={{ color: "white" }} />}
          onClick={() => {
            removeToken();
            history.push("/login");
            navigate("/login");
          }}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#f44336",
            color: "white",
            "&:hover": {
              backgroundColor: "#d32f2f",
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {" "}
      {/* Wrap everything with QueryClientProvider */}
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* AppBar for Mobile */}
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                Money Mike Backoffice
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Drawer */}
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#ffffff",
              borderRight: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          <Routes>
            <Route path="/" element={<DashboardSection />} />
            <Route path="user" element={<UserSection />} />
            <Route path="user-tree" element={<UserTreeSection />} />
            <Route path="user-tree/:userId" element={<UserTreeSection />} />
            <Route path="bd" element={<BDSection />} />
            <Route path="bd/:bdId" element={<BDDetailsSection />} />
            <Route path="sale-report" element={<SaleReportSection />} />
            <Route
              path="community-sale-report"
              element={<CommunitySaleReportSection />}
            />
            <Route path="sales" element={<SalesTransactionSection />} />
            <Route path="subscriptions" element={<SubscriptionsSection />} />
            <Route path="courses" element={<CoursesSection />} />
            <Route
              path="courses/:courseId"
              element={<CourseSeasonsSection />}
            />
            <Route
              path="courses/:courseId/seasons/:seasonId"
              element={<CourseItemsSection />}
            />
            <Route
              path="miniapp-notifications"
              element={<MiniAppNotificationsSection />}
            />
            <Route
              path="bot-notifications"
              element={<BotNotificationsSection />}
            />
          </Routes>
        </Box>
      </Box>
    </QueryClientProvider>
  );
}
